// src/pages/FundPlanner/components/Funds/PositionModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import DateRangeSelector from "components/DateRangeSelector";
import ExcelGrid from "components/ExcelGrid";
import ModalBackdrop from "components/ModalBackdrop";
import TabMenu from "components/TabMenu";
import {
  DropdownSelect,
  DateInput,
  TextInput,
  NumberInput,
} from "components/InputStyled";
import {
  TYPE_OPTIONS,
  isCusipInvestmentType,
  isEffectiveYieldOverrideInvestmentType,
  isInterestReinvestmentInvestmentType,
  normalizeInitialPositionAmount,
  requiresInitialBalanceAmount,
  isShortTermInvestmentType,
  isTransferEligibleInvestmentType,
} from "./investmentTypes";
import { calculatePurchaseCost, roundToCents } from "../../lib/money";
import { useProfile } from "context/ProfileContext";
import SmartDocDialog from "smartdoc/SmartDocDialog";
import useSmartDocAccess from "smartdoc/useSmartDocAccess";
import {
  DestinationPositionBrowser,
  DestinationPositionSelector,
  buildDestinationPositionTree as buildTransferDestinationTree,
  destinationPositionId as transferDestinationId,
  destinationPositionLabel as transferDestinationLabel,
  destinationPositionShortLabel as transferPositionLabel,
} from "./DestinationPositionBrowser";

export { buildDestinationPositionTree as buildTransferDestinationTree } from "./DestinationPositionBrowser";

export function shouldShowInitialCreditRating() {
  return true;
}

const SHORT_TERM_ACTIVITY_OPTIONS = [
  { value: "interest_income", label: "Interest Income" },
  { value: "deposit", label: "Deposit" },
  { value: "withdrawal", label: "Withdrawal" },
  { value: "fee_expense", label: "Fee Expense" },
];

const TRANSFER_ACTIVITY_OPTIONS = [
  { value: "transfer_in", label: "Transfer In" },
  { value: "transfer_out", label: "Transfer Out" },
];

const SLGS_DEMAND_ACTIVITY_OPTIONS = [
  { value: "withdrawal", label: "Withdrawal" },
];

const SLGS_DEMAND_EDIT_INTEREST_OPTION = {
  value: "interest_income",
  label: "Interest Earned",
};

const NON_SHORT_TERM_ACTIVITY_OPTIONS = [
  { value: "interest_income", label: "Interest Income" },
  { value: "sale", label: "Sell" },
  { value: "principal_paydown", label: "Principal Paydown" },
];

const CD_ACTIVITY_OPTIONS = [
  { value: "interest_income", label: "Interest Income" },
  { value: "sale", label: "Sell" },
];

const ACTIVITY_LABELS = {
  purchase: "Purchase",
  deposit: "Deposit",
  fee_expense: "Fee Expense",
  interest_income: "Interest Income",
  partial_sale: "Sell",
  principal_paydown: "Principal Paydown",
  redemption: "Principal Paydown",
  sale: "Sell",
  transfer: "Transfer",
  transfer_in: "Transfer In",
  transfer_out: "Transfer Out",
  withdrawal: "Withdrawal",
};

const POSITION_WORKFLOW_TABS = [
  { key: "activity", label: "Activity Entry" },
  { key: "updateOverride", label: "Valuation Update" },
];

const COMPACT_INPUT = "!h-7 !text-xs";
const FIELD_XS = `${COMPACT_INPUT} w-28`;
const FIELD_SM = `${COMPACT_INPUT} w-36`;
const FIELD_MD = `${COMPACT_INPUT} w-44`;
const FIELD_LG = `${COMPACT_INPUT} w-56`;
const FIELD_AMOUNT = `${COMPACT_INPUT} w-32`;
const FIELD_RATE = `${COMPACT_INPUT} w-24`;

const CD_DAY_COUNT_OPTIONS = [
  { value: "ACT/365", label: "ACT/365" },
  { value: "ACT/360", label: "ACT/360" },
  { value: "30/360", label: "30/360" },
];

const CD_INTEREST_FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semiannual" },
  { value: "annual", label: "Annual" },
  { value: "at_maturity", label: "At Maturity" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonthIso(dateText) {
  const base = dateText ? new Date(`${dateText}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  return new Date(base.getFullYear(), base.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
}

function addDaysIso(dateText, days) {
  const base = dateText ? new Date(`${dateText}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function randomUuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();

  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) =>
    (
      Number(char) ^
      (Math.random() * 16) >> (Number(char) / 4)
    ).toString(16)
  );
}

function reinvestmentGroupId(value) {
  const raw = String(value ?? "").trim();
  return raw.replace(/:(income|posting)$/i, "");
}

function normalizeIncomePeriod(activityDate, startDate, endDate) {
  const end = endDate || activityDate || todayIso();
  const start = startDate || firstDayOfMonthIso(end);

  if (!start || !end || end < start) {
    return {
      start: firstDayOfMonthIso(end),
      end,
    };
  }

  return { start, end };
}

function emptyForm() {
  return {
    investmentType: "",
    cusipOrId: "",
    description: "",
    amount: "",
    purchasePrice: "",
    purchaseAccruedInterest: "",
    rate: "",
    dayCountBasis: "ACT/365",
    interestFrequency: "at_maturity",
    initialCreditRating: "",
    maturityDate: "",
    tradeDate: "",
    settlementDate: "",
    sourceOfFundsPositionId: "",
    closedDate: "",
    issuerName: "",
    notes: "",
  };
}

function emptyValuationForm() {
  const date = todayIso();

  return {
    valuationDate: date,
    amount: "",
    price: "",
    marketValue: "",
    creditRating: "",
    effectiveYieldOverride: "",
    effectiveYieldStartDate: "",
    effectiveYieldEndDate: "",
    notes: "",
  };
}

export function isBalanceValuationInvestmentType(value) {
  return isShortTermInvestmentType(value) || isSlgsDemandDepositType(value);
}

export function balanceBeforeValuationUpdate({
  history = [],
  valuationDate,
  fallbackAmount = 0,
  excludeActivityId = "",
}) {
  const rows = Array.isArray(history) ? history : [];
  const initial = rows
    .filter(
      (row) =>
        row?.history_type === "valuation" &&
        row?.source_type === "initial_position" &&
        formatDate(row?.valuation_date || row?.history_date) <= valuationDate
    )
    .sort((a, b) =>
      formatDate(a?.valuation_date || a?.history_date).localeCompare(
        formatDate(b?.valuation_date || b?.history_date)
      )
    )[0];
  let balance = Number(
    initial?.balance_amount ??
      initial?.units ??
      initial?.par_amount ??
      fallbackAmount ??
      0
  );
  if (!Number.isFinite(balance)) balance = 0;
  balance = roundToCents(balance) ?? 0;

  rows
    .filter(
      (row) =>
        row?.history_type === "activity" &&
        row?.id !== excludeActivityId &&
        formatDate(row?.activity_date || row?.history_date) <= valuationDate
    )
    .sort((a, b) => {
      const dateCompare = formatDate(
        a?.activity_date || a?.history_date
      ).localeCompare(formatDate(b?.activity_date || b?.history_date));
      if (dateCompare) return dateCompare;
      const valuationRank = (row) =>
        row?.source_type === "valuation_update" ? 1 : 0;
      const rankCompare = valuationRank(a) - valuationRank(b);
      if (rankCompare) return rankCompare;
      return String(a?.created_at || "").localeCompare(
        String(b?.created_at || "")
      );
    })
    .forEach((row) => {
      const after = Number(row?.balance_after ?? row?.units_after);
      if (Number.isFinite(after)) {
        balance = roundToCents(after) ?? 0;
        return;
      }
      const delta = Number(row?.units_delta ?? row?.par_delta);
      if (Number.isFinite(delta)) {
        balance = roundToCents(balance + roundToCents(delta)) ?? 0;
      }
    });

  return balance;
}

function emptyActivityForm() {
  const date = todayIso();

  return {
    activityType: "interest_income",
    activityDate: date,
    unitsAmount: "",
    cashProceeds: "",
    price: "",
    reinvestInterest: false,
    incomePeriodStart: firstDayOfMonthIso(date),
    incomePeriodEnd: date,
    postingDate: addDaysIso(date, 1),
    destinationPositionId: "",
    sourcePositionId: "",
    notes: "",
  };
}

function emptySlgsAccrualForm() {
  return {
    endDate: todayIso(),
  };
}

function deriveDollarPrice(amount, purchasePrice) {
  const amt = Number(amount);
  const px = Number(purchasePrice);

  if (!Number.isFinite(amt) || amt <= 0 || !Number.isFinite(px)) {
    return "";
  }

  return calculatePurchaseCost(amt, px) ?? "";
}

export function deriveCostPlusAccrued(purchaseCost, accruedInterest) {
  if (purchaseCost === "" || purchaseCost == null) return "";

  const cost = Number(purchaseCost);
  const accrued =
    accruedInterest === "" || accruedInterest == null
      ? 0
      : Number(accruedInterest);

  if (!Number.isFinite(cost) || !Number.isFinite(accrued)) return "";
  return roundToCents(cost + accrued) ?? "";
}

function derivePurchasePrice(amount, dollarPrice) {
  const amt = Number(amount);
  const dollars = Number(dollarPrice);

  if (!Number.isFinite(amt) || amt <= 0 || !Number.isFinite(dollars)) {
    return "";
  }

  return (dollars / amt) * 100;
}

function deriveCashProceeds(unitsAmount, price) {
  return deriveDollarPrice(unitsAmount, price);
}

function deriveActivityPrice(unitsAmount, cashProceeds) {
  return derivePurchasePrice(unitsAmount, cashProceeds);
}

export function activityAmountValidationError(activityType, value) {
  const amount = Number(value);
  if (value !== "" && value != null && Number.isFinite(amount) && amount > 0) {
    return "";
  }

  const label =
    activityType === "interest_income"
      ? "Interest Amount"
      : ["transfer", "transfer_out", "transfer_in"].includes(activityType)
      ? "Transfer Amount"
      : activityType === "fee_expense"
      ? "Fee Amount"
      : activityType === "deposit" || activityType === "withdrawal"
      ? "Amount"
      : "Units / Amount";

  return `${label} must be greater than $0.`;
}

function formatNumber(value, digits = 2) {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDate(value) {
  const raw = String(value ?? "").trim();
  return raw ? raw.slice(0, 10) : "";
}

function activityLabel(value) {
  return ACTIVITY_LABELS[value] || "Activity";
}

function isTransferInRow(row = {}) {
  const externalId = String(row?.external_activity_id || "").toLowerCase();
  if (externalId.endsWith(":in")) return true;
  if (externalId.endsWith(":out")) return false;
  return Number(row?.units_delta ?? row?.cash_amount ?? 0) > 0;
}

function isTransferActivityType(value) {
  return ["transfer", "transfer_out", "transfer_in"].includes(value);
}

function interestEarnedLabel(start, end) {
  if (!start || !end) return "Interest Earned";
  return start === end
    ? `Interest Earned (${start})`
    : `Interest Earned (${start} to ${end})`;
}

export function scheduledCashflowDepositLabel(row = {}) {
  if (
    row.source_type !== "system_scheduled_cashflow" ||
    row.activity_type !== "deposit"
  ) {
    return null;
  }

  const eventKind = String(row.scheduled_event_kind || "").toLowerCase();
  const category = ["coupon", "cd_interest"].includes(eventKind)
    ? "Interest"
    : ["maturity", "cd_maturity"].includes(eventKind)
      ? "Principal"
      : null;
  const cusip = String(row.scheduled_origin_cusip || "").trim();

  if (!category) return null;
  return cusip ? `${category} Deposit - ${cusip}` : `${category} Deposit`;
}

function historyLabel(row) {
  if (
    row?.history_type === "activity" &&
    row?.source_type === "valuation_update"
  ) {
    return "Valuation Update";
  }
  if (row?.history_type === "valuation") {
    return row?.source_type === "initial_position"
      ? "Initial Position"
      : "Valuation Update";
  }
  const scheduledDepositLabel = scheduledCashflowDepositLabel(row);
  if (scheduledDepositLabel) return scheduledDepositLabel;
  if (row?.source_type === "manual_interest_reinvestment") {
    if (row?.activity_type === "deposit") {
      return "Interest Reinvested/Deposited";
    }

    const start = formatDate(row?.income_period_start);
    const end = formatDate(row?.income_period_end || row?.activity_date);
    return interestEarnedLabel(start, end);
  }
  if (row?.source_type === "position_purchase_funding") {
    return row?.notes || "Purchase Funding";
  }
  if (row?.source_type === "manual_transfer") {
    const isTransferIn = isTransferInRow(row);
    const prefix = isTransferIn ? "Transfer In" : "Transfer Out";
    const direction = isTransferIn ? "From" : "To";
    return row?.transfer_action_label
      ? `${prefix} - ${direction} ${row.transfer_action_label}`
      : prefix;
  }
  if (row?.activity_type === "interest_income" && row?.income_period_end) {
    const start = formatDate(row?.income_period_start);
    const end = formatDate(row?.income_period_end || row?.activity_date);
    return interestEarnedLabel(start, end);
  }
  if (
    row?.activity_type === "interest_income" &&
    (Number(row?.par_delta) > 0 || Number(row?.units_delta) > 0)
  ) {
    return "Interest Income - Reinvested";
  }
  return activityLabel(row?.activity_type);
}

function historyAmount(row) {
  if (row?.history_type === "valuation") {
    if (row?.source_type !== "initial_position") return "";
    return (
      row.current_face ??
      row.units ??
      row.balance_amount ??
      row.par_amount ??
      ""
    );
  }

  if (row?.activity_type === "interest_income") {
    return row.income_amount ?? row.cash_amount ?? "";
  }

  if (row?.source_type === "valuation_update") {
    return row.balance_after ?? row.units_after ?? "";
  }

  const amount = row.par_delta ?? row.units_delta ?? "";
  const n = Number(amount);
  return Number.isFinite(n) ? Math.abs(n) : amount;
}

function historyCash(row) {
  if (row?.history_type === "valuation") return row.market_value ?? "";
  if (row?.activity_type === "interest_income") {
    return row.income_amount ?? row.cash_amount ?? "";
  }
  if (row?.source_type === "manual_interest_reinvestment") {
    return row.cash_amount ?? row.par_delta ?? row.units_delta ?? "";
  }
  if (row?.source_type === "valuation_update") {
    return row.balance_after ?? row.units_after ?? "";
  }
  return row.cash_amount ?? "";
}

function historyDisplayRank(row) {
  if (row?.history_type === "valuation") {
    return row?.source_type === "initial_position" ? 2 : 0;
  }

  return 1;
}

function toPayloadValue(value) {
  return value === "" ? "" : value;
}

function defaultPurchasePriceForType(investmentType, value = "") {
  const type = String(investmentType ?? "").trim();
  return type && !isCusipInvestmentType(type) && value === "" ? 100 : value;
}

function isSlgsDemandDepositType(value) {
  const type = String(value ?? "").trim().toLowerCase();
  return type === "slgs demand deposit" || type === "demand deposit";
}

function isCdInvestmentType(value) {
  return String(value ?? "").trim().toLowerCase() === "cd";
}

export function activityOptionsForInvestmentType(investmentType) {
  if (isSlgsDemandDepositType(investmentType)) {
    return SLGS_DEMAND_ACTIVITY_OPTIONS;
  }

  if (isCdInvestmentType(investmentType)) {
    return CD_ACTIVITY_OPTIONS;
  }

  if (!isShortTermInvestmentType(investmentType)) {
    return NON_SHORT_TERM_ACTIVITY_OPTIONS;
  }

  return isTransferEligibleInvestmentType(investmentType)
    ? [
        SHORT_TERM_ACTIVITY_OPTIONS[0],
        ...TRANSFER_ACTIVITY_OPTIONS,
        ...SHORT_TERM_ACTIVITY_OPTIONS.slice(1),
      ]
    : SHORT_TERM_ACTIVITY_OPTIONS;
}

function normalizeEditableActivityType(value, investmentType) {
  const normalized = value === "redemption" ? "principal_paydown" : value;
  const options = activityOptionsForInvestmentType(investmentType);
  return options.some((option) => option.value === normalized)
    ? normalized
    : options[0]?.value || "interest_income";
}

function SectionCard({ title, description, children }) {
  return (
    <section className="rounded-lg border border-border bg-card/40">
      <div className="px-3 py-2.5 border-b border-border">
        <div className="text-sm font-semibold text-app">{title}</div>
        {description ? (
          <div className="text-xs text-muted mt-1">{description}</div>
        ) : null}
      </div>
      <div className="p-3 space-y-3">{children}</div>
    </section>
  );
}

export function TransferDestinationBrowser({
  isOpen,
  destinations,
  selectedId,
  searchText,
  expandedFundIds,
  onSearchChange,
  onToggleFund,
  onSelect,
  onClose,
  title = "Select Destination Position",
  description = "Browse eligible positions by fund.",
  showSearch = true,
  compact = false,
}) {
  const funds = useMemo(
    () => buildTransferDestinationTree(destinations, searchText),
    [destinations, searchText]
  );
  const hasSearch = !!String(searchText || "").trim();

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClose={onClose} zIndex="z-[1100]">
      <div
        className={`bg-[hsl(var(--background))] text-app rounded-lg shadow-xl border border-border w-full mx-4 flex flex-col ${
          compact ? "max-w-lg max-h-[62vh]" : "max-w-2xl max-h-[78vh]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={compact ? "px-3 py-2 border-b border-border" : "px-4 py-3 border-b border-border"}>
          <div className="text-sm font-semibold text-app">
            {title}
          </div>
          {description ? (
            <div className={`${compact ? "text-[11px]" : "text-xs"} text-muted mt-1`}>
              {description}
            </div>
          ) : null}
        </div>

        {showSearch ? (
          <div className="p-3 border-b border-border">
            <input
              type="text"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search fund, type, CUSIP / ID..."
              className="input !h-8 !text-sm w-full"
              autoFocus
            />
          </div>
        ) : null}

        <div className={`${compact ? "p-2 min-h-40" : "p-3 min-h-64"} overflow-y-auto`}>
          {funds.length ? (
            <div className="space-y-1">
              {funds.map((fund) => {
                const fundExpanded = hasSearch || expandedFundIds.has(fund.id);

                return (
                  <div key={fund.id}>
                    <button
                      type="button"
                      className={`w-full flex items-center gap-2 rounded-md px-2 text-left font-semibold hover:bg-muted/20 ${
                        compact ? "py-1 text-xs" : "py-1.5 text-sm"
                      }`}
                      onClick={() => onToggleFund(fund.id)}
                    >
                      {fundExpanded ? (
                        <ChevronDown size={15} />
                      ) : (
                        <ChevronRight size={15} />
                      )}
                      <span className="truncate">{fund.name}</span>
                    </button>

                    {fundExpanded ? (
                      <div className="ml-5 border-l border-border pl-2">
                        {fund.positions.map((destination) => {
                                    const id =
                                      transferDestinationId(destination);
                                    const selected = id === selectedId;

                                    return (
                                      <button
                                        key={id}
                                        type="button"
                                        className={`w-full rounded-md px-2 text-left hover:bg-muted/20 ${
                                          compact ? "py-1 text-xs" : "py-1.5 text-sm"
                                        } ${
                                          selected
                                            ? "bg-brand/10 text-brand font-semibold"
                                            : "text-app"
                                        }`}
                                        onClick={() => onSelect(destination)}
                                      >
                                        <div className="flex items-center justify-between gap-3">
                                          <span className="truncate">
                                            {transferPositionLabel(destination)}
                                          </span>
                                          {selected ? (
                                            <span className={compact ? "text-[10px]" : "text-xs"}>
                                              Selected
                                            </span>
                                          ) : null}
                                        </div>
                                        {destination.description ? (
                                          <div className={`${compact ? "text-[10px]" : "text-xs"} text-muted truncate`}>
                                            {destination.description}
                                          </div>
                                        ) : null}
                                      </button>
                                    );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`${compact ? "text-xs" : "text-sm"} text-muted`}>
              {showSearch
                ? "No eligible destination positions match the current search."
                : "No eligible destination positions."}
            </div>
          )}
        </div>

        <div className={`${compact ? "px-3 py-2" : "px-4 py-3"} border-t border-border flex justify-end`}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

export default function PositionModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  onSaveActivity,
  onSaveTransfer,
  onSaveValuation,
  onUpdateActivity,
  onUpdateTransfer,
  onUpdateValuation,
  onDeleteHistoryEntry,
  onGenerateSlgsDemandAccruals,
  activityHistory = [],
  transferDestinations = [],
  isLoadingTransferDestinations = false,
  isLoadingActivityHistory = false,
  isPersistedPosition = false,
  initialValues = null,
  readOnly = false,
  onSmartDocComplete,
}) {
  const { profile, selectedOrg } = useProfile();
  const smartDocAccess = useSmartDocAccess(selectedOrg?.id);
  const [form, setForm] = useState(emptyForm());
  const [valuationForm, setValuationForm] = useState(emptyValuationForm());
  const [activityForm, setActivityForm] = useState(emptyActivityForm());
  const [activityValidationError, setActivityValidationError] = useState("");
  const [slgsAccrualForm, setSlgsAccrualForm] = useState(
    emptySlgsAccrualForm()
  );
  const [editingHistoryEntry, setEditingHistoryEntry] = useState(null);
  const [historyDateRange, setHistoryDateRange] = useState({
    from: "",
    to: "",
  });
  const [isTransferBrowserOpen, setIsTransferBrowserOpen] = useState(false);
  const [isFundingBrowserOpen, setIsFundingBrowserOpen] = useState(false);
  const [fundingSearchText, setFundingSearchText] = useState("");
  const [expandedFundingFundIds, setExpandedFundingFundIds] = useState(
    () => new Set()
  );
  const [transferSearchText, setTransferSearchText] = useState("");
  const [expandedTransferFundIds, setExpandedTransferFundIds] = useState(
    () => new Set()
  );
  const [activeWorkflowTab, setActiveWorkflowTab] = useState("activity");
  const [dollarPrice, setDollarPrice] = useState("");
  const [actionNotice, setActionNotice] = useState("");
  const [isSmartDocOpen, setIsSmartDocOpen] = useState(false);
  const modalBodyRef = useRef(null);
  const positionDetailsRef = useRef(null);
  const activityEntryRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const nextValuationForm = emptyValuationForm();
    const nextActivityForm = emptyActivityForm();

    if (initialValues) {
      const nextForm = {
        investmentType: initialValues.investmentType || "",
        cusipOrId: initialValues.cusipOrId || "",
        description: initialValues.description || "",
        amount: initialValues.currentAmount ?? initialValues.amount ?? "",
        purchasePrice: defaultPurchasePriceForType(
          initialValues.investmentType,
          initialValues.purchasePrice ?? ""
        ),
        purchaseAccruedInterest: initialValues.purchaseAccruedInterest ?? "",
        rate: initialValues.rate ?? "",
        dayCountBasis: initialValues.dayCountBasis || "ACT/365",
        interestFrequency: initialValues.interestFrequency || "at_maturity",
        initialCreditRating: "",
        maturityDate: initialValues.maturityDate || "",
        tradeDate: initialValues.tradeDate || "",
        settlementDate: initialValues.settlementDate || "",
        sourceOfFundsPositionId:
          initialValues.sourceOfFundsPositionId || "",
        closedDate: initialValues.closedDate || "",
        issuerName: initialValues.issuerName || "",
        notes: initialValues.notes || "",
      };
      const nextIsCusipType = isCusipInvestmentType(nextForm.investmentType);

      nextValuationForm.valuationDate =
        nextIsCusipType && initialValues.latestValuationDate
          ? initialValues.latestValuationDate
          : nextValuationForm.valuationDate;
      nextValuationForm.price =
        nextIsCusipType &&
        initialValues.latestValuationPrice != null &&
        initialValues.latestValuationPrice !== ""
          ? initialValues.latestValuationPrice
          : nextForm.purchasePrice || "";
      nextActivityForm.reinvestInterest =
        isInterestReinvestmentInvestmentType(nextForm.investmentType);
      nextActivityForm.activityType = normalizeEditableActivityType(
        nextActivityForm.activityType,
        nextForm.investmentType
      );

      setForm(nextForm);
      setDollarPrice(
        roundToCents(initialValues.originalCost) ??
          deriveDollarPrice(nextForm.amount, nextForm.purchasePrice)
      );
      setValuationForm(nextValuationForm);
      setActivityForm(nextActivityForm);
      setActivityValidationError("");
      setSlgsAccrualForm(emptySlgsAccrualForm());
      setEditingHistoryEntry(null);
      setHistoryDateRange({ from: "", to: "" });
      setActiveWorkflowTab("activity");
      setActionNotice("");
      return;
    }

    const nextForm = {
      investmentType: "",
      cusipOrId: "",
      description: "",
      amount: "",
      purchasePrice: "",
      purchaseAccruedInterest: "",
      rate: "",
      dayCountBasis: "ACT/365",
      interestFrequency: "at_maturity",
      initialCreditRating: "",
      maturityDate: "",
      tradeDate: todayIso(),
      settlementDate: todayIso(),
      sourceOfFundsPositionId: "",
      closedDate: "",
      issuerName: "",
      notes: "",
    };

    setForm(nextForm);
    setDollarPrice("");
    setValuationForm(nextValuationForm);
    setActivityForm(nextActivityForm);
    setActivityValidationError("");
    setSlgsAccrualForm(emptySlgsAccrualForm());
    setEditingHistoryEntry(null);
    setHistoryDateRange({ from: "", to: "" });
    setActiveWorkflowTab("activity");
    setActionNotice("");
  }, [isOpen, initialValues]);

  useEffect(() => {
    if (!isOpen || !isTransferActivityType(activityForm.activityType)) {
      setIsTransferBrowserOpen(false);
      setTransferSearchText("");
    }
  }, [activityForm.activityType, isOpen]);

  const isCusipType = isCusipInvestmentType(form.investmentType);
  const canOverrideEffectiveYield = isEffectiveYieldOverrideInvestmentType(
    form.investmentType
  );
  const hasEffectiveYieldOverride =
    canOverrideEffectiveYield && valuationForm.effectiveYieldOverride !== "";
  const hasInvalidEffectiveYieldOverride =
    hasEffectiveYieldOverride &&
    (!valuationForm.effectiveYieldStartDate ||
      !valuationForm.effectiveYieldEndDate ||
      valuationForm.effectiveYieldStartDate >
        valuationForm.effectiveYieldEndDate);
  const isSlgsDemandDeposit = isSlgsDemandDepositType(form.investmentType);
  const usesBalanceValuation = isBalanceValuationInvestmentType(
    form.investmentType
  );
  const isEditMode = Boolean(initialValues);
  const canUsePositionWorkflows = isEditMode && isPersistedPosition;

  const originalAmount = useMemo(() => {
    const value = Number(initialValues?.amount);
    return Number.isFinite(value) ? value : null;
  }, [initialValues]);

  const currentAmount = Number(isEditMode ? initialValues?.amount : form.amount);
  const isEditingInitialPosition =
    editingHistoryEntry?.history_type === "valuation" &&
    editingHistoryEntry?.source_type === "initial_position";
  const initialPositionValuation = useMemo(
    () =>
      (Array.isArray(activityHistory) ? activityHistory : []).find(
        (row) =>
          row?.history_type === "valuation" &&
          row?.source_type === "initial_position"
      ) || null,
    [activityHistory]
  );
  const workflowEditingEntry = isEditingInitialPosition
    ? null
    : editingHistoryEntry;
  const positionDetailsLocked =
    readOnly || (isEditMode && !isEditingInitialPosition);
  const hasCusipParIncreaseWarning =
    isEditMode &&
    isCusipType &&
    !isEditingInitialPosition &&
    originalAmount !== null &&
    Number.isFinite(currentAmount) &&
    currentAmount > originalAmount;

  const idPlaceholder = isCusipType ? "Enter CUSIP" : "Enter short descriptor";
  const amountLabel = isCusipType ? "Amount / Par" : "Amount";
  const showPrice = isCusipType;
  const showRate = !isCusipType;
  const showDescription = !isCusipType;
  const showIssuer = !isCusipType;
  const showMaturity = !isCusipType;
  const displayedMaturity = isCusipType
    ? initialValues?.securityMaturityDate || form.maturityDate || ""
    : form.maturityDate;
  const showClosedDate = Boolean(String(form.closedDate || "").trim());
  const isIncomeActivity = activityForm.activityType === "interest_income";
  const isShortTermType = isShortTermInvestmentType(form.investmentType);
  const isCdType = isCdInvestmentType(form.investmentType);
  const usesShortTermActivityLayout =
    isShortTermType || isSlgsDemandDeposit || isCdType;
  const isShortTermIncomeActivity = usesShortTermActivityLayout && isIncomeActivity;
  const canReinvestInterest = isInterestReinvestmentInvestmentType(
    form.investmentType
  );
  const activityOptions = useMemo(
    () => {
      const options = activityOptionsForInvestmentType(form.investmentType);
      const isEditingInterestEntry =
        isSlgsDemandDeposit &&
        workflowEditingEntry?.history_type === "activity" &&
        workflowEditingEntry?.activity_type === "interest_income";

      if (
        !isEditingInterestEntry ||
        options.some((option) => option.value === "interest_income")
      ) {
        return options;
      }

      return [SLGS_DEMAND_EDIT_INTEREST_OPTION, ...options];
    },
    [
      form.investmentType,
      isSlgsDemandDeposit,
      workflowEditingEntry?.activity_type,
      workflowEditingEntry?.history_type,
    ]
  );
  const isShortTermBalanceActivity =
    usesShortTermActivityLayout &&
    (activityForm.activityType === "deposit" ||
      activityForm.activityType === "withdrawal" ||
      activityForm.activityType === "fee_expense");
  const isTransferActivity = isTransferActivityType(activityForm.activityType);
  const isTransferInActivity = activityForm.activityType === "transfer_in";
  const showActivityPriceFields =
    !isIncomeActivity && !isShortTermBalanceActivity && !isTransferActivity;
  const selectedTransferDestination = useMemo(
    () => {
      const selectedId = isTransferInActivity
        ? activityForm.sourcePositionId
        : activityForm.destinationPositionId;
      if (!selectedId) return null;

      const destination = (Array.isArray(transferDestinations)
        ? transferDestinations
        : []
      ).find(
        (candidate) => transferDestinationId(candidate) === selectedId
      );
      if (destination) return destination;

      return {
        id: selectedId,
        position_id: selectedId,
        portfolio_name: initialValues?.portfolioName || "",
        fund_name: initialValues?.fundName || "",
        investment_type: initialValues?.investmentType || "",
        cusip_or_id: initialValues?.cusipOrId || "Current Position",
      };
    },
    [
      activityForm.destinationPositionId,
      activityForm.sourcePositionId,
      initialValues?.cusipOrId,
      initialValues?.fundName,
      initialValues?.investmentType,
      initialValues?.portfolioName,
      isTransferInActivity,
      transferDestinations,
    ]
  );
  const selectedTransferDestinationLabel = selectedTransferDestination
    ? transferDestinationLabel(selectedTransferDestination)
    : "";
  const selectedSourceOfFunds = useMemo(() => {
    const selectedId = form.sourceOfFundsPositionId;
    if (!selectedId) return null;
    return (Array.isArray(transferDestinations) ? transferDestinations : []).find(
      (candidate) => transferDestinationId(candidate) === selectedId
    ) || null;
  }, [form.sourceOfFundsPositionId, transferDestinations]);
  const selectedSourceOfFundsLabel = selectedSourceOfFunds
    ? transferPositionLabel(selectedSourceOfFunds)
    : form.sourceOfFundsPositionId
      ? "Selected source position"
      : "";

  const handleField = (key, value) => {
    if (positionDetailsLocked) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleInvestmentTypeChange = (value) => {
    if (positionDetailsLocked) return;

    const nextIsCusipType = isCusipInvestmentType(value);
    setForm((prev) => ({
      ...prev,
      investmentType: value,
      amount:
        requiresInitialBalanceAmount(value) &&
        (prev.amount == null || String(prev.amount).trim() === "")
          ? 0
          : prev.amount,
      purchasePrice: defaultPurchasePriceForType(value, prev.purchasePrice),
    }));
    setValuationForm((prev) => ({
      ...prev,
      price:
        nextIsCusipType && prev.price === "" ? form.purchasePrice : prev.price,
    }));
    setActivityForm((prev) => {
      const nextOptions = activityOptionsForInvestmentType(value);
      if (nextOptions.some((option) => option.value === prev.activityType)) {
        return {
          ...prev,
          reinvestInterest: isInterestReinvestmentInvestmentType(value)
            ? prev.reinvestInterest || prev.activityType === "interest_income"
            : false,
        };
      }
      return {
        ...prev,
        activityType: nextOptions[0]?.value || "interest_income",
        price: "",
        cashProceeds: "",
        reinvestInterest:
          nextOptions[0]?.value === "interest_income" &&
          isInterestReinvestmentInvestmentType(value),
      };
    });
  };

  const handleValuationField = (key, value) => {
    if (readOnly) return;
    setValuationForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSlgsAccrualField = (key, value) => {
    if (readOnly) return;
    setSlgsAccrualForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleValuationPriceChange = (value) => {
    if (readOnly) return;

    setValuationForm((prev) => ({
      ...prev,
      price: value,
      marketValue:
        form.amount === ""
          ? prev.marketValue
          : deriveDollarPrice(form.amount, value),
    }));
  };

  const handleValuationMarketValueChange = (value) => {
    if (readOnly) return;

    const nextPrice = derivePurchasePrice(form.amount, value);
    setValuationForm((prev) => ({
      ...prev,
      marketValue: value,
      price: nextPrice === "" ? prev.price : nextPrice,
    }));
  };

  const handleActivityField = useCallback((key, value) => {
    if (readOnly) return;

    if (key === "activityType") setActivityValidationError("");

    setActivityForm((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "activityDate") {
        next.incomePeriodEnd = value;
        next.incomePeriodStart = firstDayOfMonthIso(value);
        next.postingDate = addDaysIso(value, 1);
      }

      if (key === "incomePeriodEnd") {
        next.activityDate = value;
        next.incomePeriodStart = firstDayOfMonthIso(value);
        next.postingDate = addDaysIso(value, 1);
      }

      if (key === "activityType") {
        next.reinvestInterest =
          value === "interest_income" &&
          isInterestReinvestmentInvestmentType(form.investmentType);

        if (value === "interest_income") {
          next.price = "";
          next.cashProceeds = "";
        } else if (
          value === "deposit" ||
          value === "withdrawal" ||
          value === "fee_expense"
        ) {
          next.price = "";
          next.cashProceeds = prev.unitsAmount;
        }
      }

      if (key === "reinvestInterest" && value && !prev.postingDate) {
        next.postingDate = addDaysIso(prev.incomePeriodEnd, 1);
      }

      return next;
    });
  }, [form.investmentType, readOnly]);

  const openTransferBrowser = useCallback(() => {
    if (readOnly || isLoadingTransferDestinations) return;
    setTransferSearchText("");
    setExpandedTransferFundIds(
      new Set(
        selectedTransferDestination?.fund_id
          ? [selectedTransferDestination.fund_id]
          : []
      )
    );
    setIsTransferBrowserOpen(true);
  }, [
    isLoadingTransferDestinations,
    readOnly,
    selectedTransferDestination?.fund_id,
  ]);

  const closeTransferBrowser = useCallback(() => {
    setIsTransferBrowserOpen(false);
  }, []);

  const handleSelectTransferDestination = useCallback(
    (destination) => {
      const id = transferDestinationId(destination);
      if (!id || readOnly) return;

      handleActivityField(
        activityForm.activityType === "transfer_in"
          ? "sourcePositionId"
          : "destinationPositionId",
        id
      );
      setIsTransferBrowserOpen(false);
    },
    [activityForm.activityType, handleActivityField, readOnly]
  );

  const toggleTransferFund = useCallback((fundId) => {
    setExpandedTransferFundIds((prev) => {
      const next = new Set(prev);
      if (next.has(fundId)) {
        next.delete(fundId);
      } else {
        next.add(fundId);
      }
      return next;
    });
  }, []);

  const handleActivityUnitsAmountChange = (value) => {
    if (readOnly) return;

    setActivityValidationError("");

    setActivityForm((prev) => ({
      ...prev,
      unitsAmount: value,
      cashProceeds:
        prev.activityType === "deposit" ||
        prev.activityType === "withdrawal" ||
        prev.activityType === "fee_expense" ||
        isTransferActivityType(prev.activityType)
          ? value
          : prev.price === ""
          ? prev.cashProceeds
          : deriveCashProceeds(value, prev.price),
    }));
  };

  const handleActivityPriceChange = (value) => {
    if (readOnly) return;

    setActivityForm((prev) => ({
      ...prev,
      price: value,
      cashProceeds:
        prev.unitsAmount === ""
          ? prev.cashProceeds
          : deriveCashProceeds(prev.unitsAmount, value),
    }));
  };

  const handleCashProceedsChange = (value) => {
    if (readOnly) return;

    setActivityForm((prev) => {
      const nextPrice = deriveActivityPrice(prev.unitsAmount, value);

      return {
        ...prev,
        cashProceeds: value,
        price: nextPrice === "" ? prev.price : nextPrice,
      };
    });
  };

  const handleAmountChange = (value) => {
    if (positionDetailsLocked) return;
    setForm((prev) => ({ ...prev, amount: value }));
    setDollarPrice(deriveDollarPrice(value, form.purchasePrice));
  };

  const handlePurchasePriceChange = (value) => {
    if (positionDetailsLocked) return;
    setForm((prev) => ({ ...prev, purchasePrice: value }));
    setDollarPrice(deriveDollarPrice(form.amount, value));
  };

  const handleDollarPriceChange = (value) => {
    if (positionDetailsLocked) return;
    setDollarPrice(value);

    const nextPurchasePrice = derivePurchasePrice(form.amount, value);
    if (nextPurchasePrice === "") return;

    setForm((prev) => ({
      ...prev,
      purchasePrice: nextPurchasePrice,
    }));
  };

  const resetPositionDetailsFromInitialValues = () => {
    if (!initialValues) return;

    const nextForm = {
      investmentType: initialValues.investmentType || "",
      cusipOrId: initialValues.cusipOrId || "",
      description: initialValues.description || "",
      amount: initialValues.currentAmount ?? initialValues.amount ?? "",
      purchasePrice: defaultPurchasePriceForType(
        initialValues.investmentType,
        initialValues.purchasePrice ?? ""
      ),
      purchaseAccruedInterest: initialValues.purchaseAccruedInterest ?? "",
      rate: initialValues.rate ?? "",
      initialCreditRating: "",
      maturityDate: initialValues.maturityDate || "",
      tradeDate: initialValues.tradeDate || "",
      settlementDate: initialValues.settlementDate || "",
      sourceOfFundsPositionId:
        initialValues.sourceOfFundsPositionId || "",
      closedDate: initialValues.closedDate || "",
      issuerName: initialValues.issuerName || "",
      notes: initialValues.notes || "",
    };

    setForm(nextForm);
    setDollarPrice(
      roundToCents(initialValues.originalCost) ??
        deriveDollarPrice(nextForm.amount, nextForm.purchasePrice)
    );
  };

  const handleClose = () => {
    onClose?.();
  };

  const buildPositionPayload = () => ({
    investment_type: form.investmentType,
    cusip_or_id: String(form.cusipOrId || "").trim(),
    raw_security_description: String(form.description || "").trim(),
    par_amount: toPayloadValue(
      normalizeInitialPositionAmount(
        form.investmentType,
        isEditMode && !isEditingInitialPosition
          ? initialValues?.amount
          : form.amount
      )
    ),
    purchase_price: toPayloadValue(form.purchasePrice),
    original_cost: isCusipType ? toPayloadValue(roundToCents(dollarPrice)) : "",
    purchase_accrued_interest: isCusipType
      ? toPayloadValue(form.purchaseAccruedInterest)
      : "",
    raw_coupon: toPayloadValue(form.rate),
    day_count_basis: isCdInvestmentType(form.investmentType)
      ? form.dayCountBasis || "ACT/365"
      : "",
    interest_frequency: isCdInvestmentType(form.investmentType)
      ? form.interestFrequency || "at_maturity"
      : "",
    raw_maturity_date: form.maturityDate || "",
    purchase_date: form.tradeDate || "",
    settlement_date: form.settlementDate || "",
    source_of_funds_position_id: form.sourceOfFundsPositionId || "",
    closed_date: form.closedDate || "",
    raw_issuer_name: String(form.issuerName || "").trim(),
    notes: String(form.notes || "").trim(),
    initial_rating_rollup: String(form.initialCreditRating || "").trim(),
    initial_rating_rollup_explicit: isEditingInitialPosition,
  });

  const handleUpdateInitialPosition = async () => {
    const positionResult = await onSave?.(buildPositionPayload());
    if (positionResult && positionResult.ok === false) return;
    setActionNotice("Position changes applied. Select Save Fund to commit them.");
    clearHistoryEdit();
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!form.investmentType || !String(form.cusipOrId || "").trim()) return;
    if (hasCusipParIncreaseWarning) return;

    onSave?.(buildPositionPayload());
  };

  const clearHistoryEdit = () => {
    setEditingHistoryEntry(null);
    setActivityValidationError("");
    setValuationForm(emptyValuationForm());
    setActivityForm({
      ...emptyActivityForm(),
      activityType: normalizeEditableActivityType(
        "interest_income",
        form.investmentType
      ),
      reinvestInterest: isInterestReinvestmentInvestmentType(
        form.investmentType
      ),
    });
  };

  const cancelInitialPositionEdit = () => {
    setIsFundingBrowserOpen(false);
    resetPositionDetailsFromInitialValues();
    clearHistoryEdit();
  };

  const handleSaveValuation = async () => {
    if (readOnly || !canUsePositionWorkflows) return;

    if (
      usesBalanceValuation &&
      workflowEditingEntry?.history_type !== "valuation"
    ) {
      const updatedBalance = Number(valuationForm.marketValue);
      if (!Number.isFinite(updatedBalance) || updatedBalance < 0) {
        setActivityValidationError(
          "Updated Balance must be a non-negative dollar amount."
        );
        return;
      }

      const valuationDate = valuationForm.valuationDate || todayIso();
      const previousBalance = balanceBeforeValuationUpdate({
        history: activityHistory,
        valuationDate,
        fallbackAmount: initialValues?.amount,
        excludeActivityId:
          workflowEditingEntry?.history_type === "activity"
            ? workflowEditingEntry.id
            : "",
      });
      const adjustment = updatedBalance - previousBalance;
      const hasAdjustment = Math.abs(adjustment) >= 0.005;

      if (hasAdjustment) {
        const activityPayload = {
          activity_type: adjustment >= 0 ? "deposit" : "withdrawal",
          activity_date: valuationDate,
          trade_date: "",
          settlement_date: "",
          par_delta: "",
          units_delta: adjustment,
          cash_amount: adjustment,
          price: "",
          balance_after: updatedBalance,
          income_amount: "",
          income_period_start: "",
          income_period_end: "",
          source_type: "valuation_update",
          source_batch_id: "",
          external_activity_id: "",
          notes: String(valuationForm.notes || "").trim(),
        };
        const activityResult =
          workflowEditingEntry?.history_type === "activity"
            ? await onUpdateActivity?.(workflowEditingEntry.id, activityPayload, {
                previousActivityDate:
                  workflowEditingEntry.activity_date || "",
                previousActivityType:
                  workflowEditingEntry.activity_type || "",
              })
            : await onSaveActivity?.(activityPayload, {});
        if (!activityResult?.ok) return;
      } else if (workflowEditingEntry?.history_type === "activity") {
        setActivityValidationError(
          "Updated Balance must differ from the balance before this update."
        );
        return;
      }

      const hasValuationMetadata =
        String(valuationForm.creditRating || "").trim() ||
        hasEffectiveYieldOverride;
      if (hasValuationMetadata && onSaveValuation) {
        const metadataResult = await onSaveValuation({
          valuation_date: valuationDate,
          par_amount: "",
          current_face: "",
          units: "",
          balance_amount: "",
          price: "",
          market_value: "",
          book_value: "",
          accrued_interest: "",
          effective_yield_override: hasEffectiveYieldOverride
            ? Number(valuationForm.effectiveYieldOverride) / 100
            : "",
          effective_yield_start_date: hasEffectiveYieldOverride
            ? valuationForm.effectiveYieldStartDate || ""
            : "",
          effective_yield_end_date: hasEffectiveYieldOverride
            ? valuationForm.effectiveYieldEndDate || ""
            : "",
          rating_rollup: String(valuationForm.creditRating || "").trim(),
          source_type: "manual",
          is_override: true,
          notes: String(valuationForm.notes || "").trim(),
        });
        if (!metadataResult?.ok) return;
      }

      if (!hasAdjustment && !hasValuationMetadata) {
        setActivityValidationError(
          "Updated Balance must differ from the current balance."
        );
        return;
      }
      setActivityValidationError("");
      setActionNotice("Update recorded.");
      clearHistoryEdit();
      return;
    }

    if (!onSaveValuation) return;

    const payload = {
      valuation_date: valuationForm.valuationDate || todayIso(),
      par_amount: "",
      current_face: "",
      units: "",
      balance_amount: "",
      price: isCusipType ? toPayloadValue(valuationForm.price) : "",
      market_value: toPayloadValue(valuationForm.marketValue),
      book_value: "",
      accrued_interest: "",
      effective_yield_override: canOverrideEffectiveYield
        ? toPayloadValue(
            valuationForm.effectiveYieldOverride === ""
              ? ""
              : Number(valuationForm.effectiveYieldOverride) / 100
          )
        : "",
      effective_yield_start_date: hasEffectiveYieldOverride
        ? valuationForm.effectiveYieldStartDate || ""
        : "",
      effective_yield_end_date: hasEffectiveYieldOverride
        ? valuationForm.effectiveYieldEndDate || ""
        : "",
      rating_rollup: String(valuationForm.creditRating || "").trim(),
      source_type: "manual",
      is_override: true,
      notes: String(valuationForm.notes || "").trim(),
    };

    const result =
      workflowEditingEntry?.history_type === "valuation"
        ? await onUpdateValuation?.(workflowEditingEntry.id, payload, {
            previousValuationDate: workflowEditingEntry.valuation_date || "",
            previousSourceType: workflowEditingEntry.source_type || "",
          })
        : await onSaveValuation?.(payload, {});

    if (result?.ok) {
      setActionNotice("Valuation recorded.");
      clearHistoryEdit();
    }
  };

  const handleGenerateSlgsDemandAccruals = async () => {
    if (
      readOnly ||
      !canUsePositionWorkflows ||
      !isSlgsDemandDeposit ||
      !onGenerateSlgsDemandAccruals
    ) {
      return;
    }

    const result = await onGenerateSlgsDemandAccruals({
      endDate: slgsAccrualForm.endDate || todayIso(),
    });
    if (result?.ok) setActionNotice("Daily interest recorded.");
  };

  const handleSaveActivity = async () => {
    if (readOnly || !canUsePositionWorkflows) return;
    if (isTransferActivityType(activityForm.activityType) && !onSaveTransfer) return;
    if (!isTransferActivityType(activityForm.activityType) && !onSaveActivity) return;

    const amountError = activityAmountValidationError(
      activityForm.activityType,
      activityForm.unitsAmount
    );
    if (amountError) {
      setActivityValidationError(amountError);
      return;
    }
    setActivityValidationError("");

    const amountValue = Number(activityForm.unitsAmount);
    const absoluteAmount =
      activityForm.unitsAmount === "" || !Number.isFinite(amountValue)
        ? ""
        : Math.abs(amountValue);
    const saleAmount =
      absoluteAmount === ""
        ? ""
        : activityForm.activityType === "deposit"
        ? absoluteAmount
        : -absoluteAmount;
    const isIncomeActivity = activityForm.activityType === "interest_income";
    const isTransferActivity = isTransferActivityType(activityForm.activityType);
    if (isTransferActivity) {
      const transferIn = activityForm.activityType === "transfer_in";
      const transferPayload = {
        source_position_id: transferIn
          ? activityForm.sourcePositionId
          : workflowEditingEntry?.transfer_source_position_id || initialValues?.positionId || "",
        destination_position_id: transferIn
          ? initialValues?.positionId || ""
          : activityForm.destinationPositionId,
        activity_date: activityForm.activityDate || todayIso(),
        amount: absoluteAmount,
        notes: String(activityForm.notes || "").trim(),
        replace_external_activity_id:
          workflowEditingEntry?.source_type === "manual_transfer"
            ? workflowEditingEntry.external_activity_id
            : "",
      };

      const result =
        workflowEditingEntry?.history_type === "activity"
          ? await onUpdateTransfer?.(workflowEditingEntry.id, transferPayload, {
              previousActivityDate: workflowEditingEntry.activity_date || "",
              previousActivityType: workflowEditingEntry.activity_type || "",
            })
          : await onSaveTransfer?.(transferPayload, {});

      if (result?.ok) {
        setActionNotice("Transfer recorded.");
        clearHistoryEdit();
      }
      return;
    }

    const isBalanceMovement =
      activityForm.activityType === "deposit" ||
      activityForm.activityType === "withdrawal" ||
      activityForm.activityType === "fee_expense";
    const cashAmount =
      isIncomeActivity
        ? ""
        : isBalanceMovement
        ? saleAmount
        : toPayloadValue(activityForm.cashProceeds);
    const reinvestAmount =
      isIncomeActivity &&
      canReinvestInterest &&
      activityForm.reinvestInterest &&
      activityForm.unitsAmount !== "" &&
      Number.isFinite(amountValue)
        ? Math.abs(amountValue)
        : "";
    const incomePeriod = isIncomeActivity
      ? normalizeIncomePeriod(
          usesShortTermActivityLayout
            ? activityForm.incomePeriodEnd
            : activityForm.activityDate,
          activityForm.incomePeriodStart,
          activityForm.incomePeriodEnd
        )
      : { start: "", end: "" };
    const externalActivityId =
      reinvestmentGroupId(workflowEditingEntry?.external_activity_id) ||
      randomUuid();
    const isLinkedInterestReinvestment =
      (isShortTermType || isCdType) &&
      isIncomeActivity &&
      canReinvestInterest &&
      activityForm.reinvestInterest &&
      reinvestAmount !== "";

    const payload = {
      activity_type: activityForm.activityType,
      activity_date:
        isShortTermIncomeActivity
          ? incomePeriod.end || todayIso()
          : activityForm.activityDate || todayIso(),
      trade_date: "",
      settlement_date: "",
      par_delta:
        isCusipType
          ? isIncomeActivity
            ? isLinkedInterestReinvestment
              ? ""
              : reinvestAmount
            : saleAmount
          : "",
      units_delta:
        !isCusipType
          ? isIncomeActivity
            ? isLinkedInterestReinvestment
              ? ""
              : reinvestAmount
            : saleAmount
          : "",
      cash_amount: cashAmount,
      price:
        isIncomeActivity || isBalanceMovement
          ? ""
          : toPayloadValue(activityForm.price),
      income_amount:
        isIncomeActivity ? toPayloadValue(activityForm.unitsAmount) : "",
      income_period_start: incomePeriod.start,
      income_period_end: incomePeriod.end,
      source_type: isLinkedInterestReinvestment
        ? "manual_interest_reinvestment"
        : "manual",
      source_batch_id: "",
      external_activity_id: isLinkedInterestReinvestment
        ? `${externalActivityId}:income`
        : "",
      notes: String(activityForm.notes || "").trim(),
    };
    const activityPayload = isLinkedInterestReinvestment
      ? {
          replace_external_activity_id:
            workflowEditingEntry?.source_type === "manual_interest_reinvestment"
              ? workflowEditingEntry.external_activity_id
              : "",
          activities: [
            payload,
            {
              activity_type: "deposit",
              activity_date:
                activityForm.postingDate ||
                addDaysIso(incomePeriod.end, 1) ||
                incomePeriod.end ||
                todayIso(),
              trade_date: "",
              settlement_date: "",
              par_delta: isCusipType ? reinvestAmount : "",
              units_delta: !isCusipType ? reinvestAmount : "",
              cash_amount: "",
              price: "",
              income_amount: "",
              income_period_start: "",
              income_period_end: "",
              source_type: "manual_interest_reinvestment",
              source_batch_id: "",
              external_activity_id: `${externalActivityId}:posting`,
              notes: String(activityForm.notes || "").trim(),
            },
          ],
        }
      : {
          ...payload,
          replace_external_activity_id:
            workflowEditingEntry?.source_type === "manual_interest_reinvestment"
              ? workflowEditingEntry.external_activity_id
              : "",
        };

    const result =
      workflowEditingEntry?.history_type === "activity"
        ? await onUpdateActivity?.(workflowEditingEntry.id, activityPayload, {
            previousActivityDate: workflowEditingEntry.activity_date || "",
            previousActivityType: workflowEditingEntry.activity_type || "",
          })
        : await onSaveActivity?.(activityPayload, {});

    if (result?.ok) {
      setActionNotice("Activity recorded.");
      clearHistoryEdit();
    }
  };

  const handleEditHistory = useCallback((row) => {
    if (readOnly || !row?.source_id) return;

    setEditingHistoryEntry({
      id: row.source_id,
      history_type: row.history_type,
      source_type: row.source_type || "",
      external_activity_id: row.external_activity_id || "",
      activity_date: row.activity_date || "",
      activity_type: row.activity_type || "",
      valuation_date: row.valuation_date || "",
    });

    if (row.history_type === "valuation") {
      if (row.source_type === "initial_position") {
        const initialAmount =
          row.current_face ??
          row.units ??
          row.balance_amount ??
          row.par_amount ??
          "";

        setForm((prev) => ({
          ...prev,
          amount: initialAmount,
          initialCreditRating: row.rating_rollup || "",
        }));
        setDollarPrice(deriveDollarPrice(initialAmount, form.purchasePrice));
        setValuationForm(emptyValuationForm());
        setActivityForm({
          ...emptyActivityForm(),
          activityType: normalizeEditableActivityType(
            "interest_income",
            form.investmentType
          ),
          reinvestInterest: isInterestReinvestmentInvestmentType(
            form.investmentType
          ),
        });
        setActiveWorkflowTab("activity");
        window.setTimeout(() => {
          modalBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          positionDetailsRef.current?.focus({ preventScroll: true });
        }, 0);
        return;
      }

      setActiveWorkflowTab("updateOverride");
      setValuationForm({
        valuationDate: row.valuation_date || todayIso(),
        amount:
          row.current_face ??
          row.units ??
          row.balance_amount ??
          row.par_amount ??
          "",
        price: row.price ?? "",
        marketValue: row.market_value ?? "",
        creditRating: row.rating_rollup || "",
        effectiveYieldOverride:
          row.effective_yield_override == null
            ? ""
            : Number(row.effective_yield_override) * 100,
        effectiveYieldStartDate: row.effective_yield_start_date || "",
        effectiveYieldEndDate: row.effective_yield_end_date || "",
        notes: row.notes || "",
      });
      return;
    }

    if (row.source_type === "valuation_update") {
      setActiveWorkflowTab("updateOverride");
      setValuationForm({
        ...emptyValuationForm(),
        valuationDate: row.activity_date || todayIso(),
        marketValue: row.balance_after ?? row.units_after ?? "",
        notes: row.notes || "",
      });
      return;
    }

    setActiveWorkflowTab("activity");

    if (row.source_type === "manual_transfer") {
      const counterparty = row.transfer_counterparty || {};
      const isTransferIn = isTransferInRow(row);
      const sourcePositionId = isTransferIn
        ? counterparty.position_id || ""
        : row.position_id || initialValues?.positionId || "";
      const destinationPositionId = isTransferIn
        ? row.position_id || initialValues?.positionId || ""
        : counterparty.position_id || "";
      const amount = row.units_delta ?? row.cash_amount ?? "";
      const amountNumber = Number(amount);

      setEditingHistoryEntry((prev) => ({
        ...prev,
        transfer_source_position_id: sourcePositionId,
      }));
      setActivityForm({
        ...emptyActivityForm(),
        activityType: isTransferIn ? "transfer_in" : "transfer_out",
        activityDate: row.activity_date || todayIso(),
        unitsAmount: Number.isFinite(amountNumber) ? Math.abs(amountNumber) : "",
        cashProceeds: "",
        price: "",
        reinvestInterest: false,
        destinationPositionId,
        sourcePositionId,
        notes: row.notes || "",
      });
      window.setTimeout(() => {
        activityEntryRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        activityEntryRef.current?.focus({ preventScroll: true });
      }, 0);
      return;
    }

    const companionReinvestmentIncome =
      row.source_type === "manual_interest_reinvestment" &&
      row.external_activity_id
        ? activityHistory.find(
            (entry) =>
              entry?.history_type === "activity" &&
              reinvestmentGroupId(entry?.external_activity_id) ===
                reinvestmentGroupId(row.external_activity_id) &&
              entry?.activity_type === "interest_income"
          )
        : null;
    const companionReinvestmentDeposit =
      row.source_type === "manual_interest_reinvestment" &&
      row.external_activity_id
        ? activityHistory.find(
            (entry) =>
              entry?.history_type === "activity" &&
              reinvestmentGroupId(entry?.external_activity_id) ===
                reinvestmentGroupId(row.external_activity_id) &&
              entry?.activity_type === "deposit"
          )
        : null;
    const incomeRow =
      row.activity_type === "interest_income"
        ? row
        : companionReinvestmentIncome || row;
    const principalRow =
      row.activity_type === "deposit"
        ? row
        : companionReinvestmentDeposit || row;
    const amount =
      incomeRow.activity_type === "interest_income"
        ? incomeRow.income_amount
        : principalRow.par_delta ?? principalRow.units_delta;
    const amountNumber = Number(amount);
    const reinvestAmount = Number(
      principalRow.par_delta ?? principalRow.units_delta
    );
    const rawActivityType =
      row.source_type === "manual_interest_reinvestment" &&
      row.activity_type === "deposit"
        ? "interest_income"
        : row.activity_type || "interest_income";
    const nextActivityType =
      isSlgsDemandDeposit && rawActivityType === "interest_income"
        ? "interest_income"
        : normalizeEditableActivityType(rawActivityType, form.investmentType);

    setActivityForm({
      activityType: nextActivityType,
      activityDate: incomeRow.activity_date || row.activity_date || todayIso(),
      unitsAmount: Number.isFinite(amountNumber) ? Math.abs(amountNumber) : "",
      cashProceeds: principalRow.cash_amount ?? row.cash_amount ?? "",
      price:
        nextActivityType === "deposit" ||
        nextActivityType === "withdrawal" ||
        nextActivityType === "fee_expense"
          ? ""
          : incomeRow.price ?? row.price ?? "",
      reinvestInterest:
        row.source_type === "manual_interest_reinvestment" ||
        (row.activity_type === "interest_income" &&
          Number.isFinite(reinvestAmount) &&
          reinvestAmount > 0),
      incomePeriodStart:
        incomeRow.income_period_start || firstDayOfMonthIso(todayIso()),
      incomePeriodEnd:
        incomeRow.income_period_end || incomeRow.activity_date || todayIso(),
      postingDate:
        principalRow?.activity_date ||
        (row.activity_type === "deposit"
          ? row.activity_date
          : addDaysIso(row.activity_date || todayIso(), 1)),
      notes: incomeRow.notes || row.notes || "",
    });
    window.setTimeout(() => {
      activityEntryRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      activityEntryRef.current?.focus({ preventScroll: true });
    }, 0);
  }, [
    activityHistory,
    form.investmentType,
    form.purchasePrice,
    initialValues?.positionId,
    isSlgsDemandDeposit,
    readOnly,
  ]);

  const historyRows = useMemo(
    () =>
      (Array.isArray(activityHistory) ? activityHistory : [])
        .map((row) => ({
          ...row,
          id: `${row.history_type}_${row.id}`,
          source_id: row.id,
          date: formatDate(row.history_date),
          action: historyLabel(row),
          amount: historyAmount(row),
          cash_value: historyCash(row),
        }))
        .sort((a, b) => {
          const dateCompare = String(b.history_date || "").localeCompare(
            String(a.history_date || "")
          );
          if (dateCompare) return dateCompare;

          const rankCompare = historyDisplayRank(a) - historyDisplayRank(b);
          if (rankCompare) return rankCompare;

          return String(b.created_at || "").localeCompare(
            String(a.created_at || "")
          );
        }),
    [activityHistory]
  );

  const filteredHistoryRows = useMemo(() => {
    const from = historyDateRange.from || "";
    const to = historyDateRange.to || "";

    if (!from && !to) return historyRows;

    return historyRows.filter((row) => {
      const date = formatDate(row.history_date || row.date);
      if (!date) return false;
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    });
  }, [historyDateRange, historyRows]);

  const handleHistoryDateRangeChange = useCallback((from, to) => {
    setHistoryDateRange({ from: from || "", to: to || "" });
  }, []);

  const historyColumns = useMemo(
    () => [
      {
        key: "date",
        label: "Date",
        editable: false,
        date: true,
        width: "w-32",
        className: "font-mono-app",
      },
      {
        key: "action",
        label: "Action",
        editable: false,
        width: "w-56",
      },
      {
        key: "amount",
        label: "Units / Amount",
        editable: false,
        numeric: true,
        format: (v) => formatNumber(v, 2),
        className: "font-mono-app text-right",
        width: "w-36",
      },
      {
        key: "price",
        label: "Price",
        editable: false,
        numeric: true,
        format: (v) => formatNumber(v, 8),
        className: "font-mono-app text-right",
        width: "w-32",
      },
      {
        key: "cash_value",
        label: "Cash / Value",
        editable: false,
        numeric: true,
        format: (v) => formatNumber(v, 2),
        className: "font-mono-app text-right",
        width: "w-36",
      },
      {
        key: "actions",
        label: "",
        editable: false,
        width: "w-24",
        render: (row) =>
          !readOnly && row.source_type !== "position_purchase_funding" ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleEditHistory(row)}
                className="text-app hover:text-brand"
                title="Edit history entry"
              >
                <Pencil size={16} />
              </button>
              {row.source_type !== "initial_position" ? (
                <button
                  type="button"
                  onClick={async () => {
                    const result = await onDeleteHistoryEntry?.(row);
                    if (result?.ok) setActionNotice("History entry deleted.");
                  }}
                  className="text-red-500 hover:text-red-700"
                  title="Delete history entry"
                >
                  <Trash2 size={16} />
                </button>
              ) : null}
            </div>
          ) : null,
      },
    ],
    [handleEditHistory, onDeleteHistoryEntry, readOnly]
  );

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClose={handleClose}>
      <div
        className="bg-[hsl(var(--background))] text-app rounded-xl shadow-xl border border-border w-full max-w-4xl mx-4 max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-border">
          <h3 className="text-app font-semibold">
            {initialValues ? "Edit Position" : "Add Position"}
          </h3>
          <div className="text-xs text-muted mt-1">
            Enter original position details and, for existing positions, record
            dated activity, valuation, and income updates.
          </div>
        </div>

        <div ref={modalBodyRef} className="p-4 space-y-4 overflow-y-auto">
          <SectionCard
            title="Position Details"
            description="Changes in this section are applied to the fund draft and committed when you select Save Fund."
          >
            <div
              ref={positionDetailsRef}
              tabIndex={-1}
              className="flex flex-wrap gap-3 items-end outline-none"
            >
              <DropdownSelect
                label="Type"
                value={form.investmentType}
                onChange={handleInvestmentTypeChange}
                options={TYPE_OPTIONS}
                placeholder="Select..."
                disabled={positionDetailsLocked}
                className="w-56"
                controlClassName={COMPACT_INPUT}
              />

              <TextInput
                label="CUSIP / ID"
                value={form.cusipOrId}
                onChange={(v) => handleField("cusipOrId", v)}
                placeholder={idPlaceholder}
                disabled={positionDetailsLocked}
                className={FIELD_MD}
              />

              <NumberInput
                label={amountLabel}
                value={form.amount}
                onChange={handleAmountChange}
                type="decimal"
                disabled={positionDetailsLocked}
                className={FIELD_AMOUNT}
              />

              {isCusipType ? (
                <DateInput
                  label="Maturity"
                  value={displayedMaturity}
                  onChange={() => {}}
                  disabled
                  className={FIELD_SM}
                />
              ) : null}

              {showPrice ? (
                <div className="flex items-end gap-2">
                  <NumberInput
                    label="Purchase Price (Par=100)"
                    value={form.purchasePrice}
                    onChange={handlePurchasePriceChange}
                    type="decimal"
                    precision={8}
                    disabled={positionDetailsLocked}
                    className={FIELD_RATE}
                  />

                  <div className="h-7 flex items-center text-xs font-semibold text-muted">
                    OR
                  </div>

                  <NumberInput
                    label="Purchase Cost ($)"
                    value={dollarPrice}
                    onChange={handleDollarPriceChange}
                    type="currency"
                    precision={2}
                    disabled={positionDetailsLocked}
                    className={FIELD_AMOUNT}
                  />

                  <NumberInput
                    label="Accrued Interest"
                    value={form.purchaseAccruedInterest}
                    onChange={(v) => handleField("purchaseAccruedInterest", v)}
                    type="currency"
                    precision={2}
                    disabled={positionDetailsLocked}
                    className={FIELD_AMOUNT}
                  />

                  <NumberInput
                    label="Cost + Accrued"
                    value={deriveCostPlusAccrued(
                      dollarPrice,
                      form.purchaseAccruedInterest
                    )}
                    type="currency"
                    precision={2}
                    readOnly
                    className={`${FIELD_AMOUNT} !bg-muted/40 !text-muted !cursor-default`}
                  />
                </div>
              ) : null}

              {showRate ? (
                <NumberInput
                  label="Rate"
                  value={form.rate}
                  onChange={(v) => handleField("rate", v)}
                  type="percent"
                  disabled={positionDetailsLocked}
                  className={FIELD_RATE}
                />
              ) : null}

              {isCdType ? (
                <>
                  <DropdownSelect
                    label="Day Count"
                    value={form.dayCountBasis}
                    onChange={(v) => handleField("dayCountBasis", v)}
                    options={CD_DAY_COUNT_OPTIONS}
                    disabled={positionDetailsLocked}
                    className={FIELD_SM}
                  />
                  <DropdownSelect
                    label="Interest Frequency"
                    value={form.interestFrequency}
                    onChange={(v) => handleField("interestFrequency", v)}
                    options={CD_INTEREST_FREQUENCY_OPTIONS}
                    disabled={positionDetailsLocked}
                    className={FIELD_MD}
                  />
                </>
              ) : null}

              {shouldShowInitialCreditRating() ? (
                <TextInput
                  label="Initial Credit Rating"
                  value={
                    isEditMode && !isEditingInitialPosition
                      ? initialPositionValuation?.rating_rollup ||
                        form.initialCreditRating
                      : form.initialCreditRating
                  }
                  onChange={(v) => handleField("initialCreditRating", v)}
                  placeholder="e.g., AAA"
                  disabled={positionDetailsLocked}
                  className={FIELD_XS}
                />
              ) : null}

            </div>

            {hasCusipParIncreaseWarning ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Additional purchases of an existing CUSIP should be entered as a
                new position/lot with its own purchase price and settlement
                date.
              </div>
            ) : null}

            {showDescription || showIssuer || showMaturity ? (
              <div className="flex flex-wrap gap-3 items-end">
                {showDescription ? (
                  <TextInput
                    label="Description"
                    value={form.description}
                    onChange={(v) => handleField("description", v)}
                    placeholder="Optional description"
                    disabled={positionDetailsLocked}
                    className={FIELD_LG}
                  />
                ) : null}

                {showIssuer ? (
                  <TextInput
                    label="Issuer / Provider"
                    value={form.issuerName}
                    onChange={(v) => handleField("issuerName", v)}
                    placeholder="Optional"
                    disabled={positionDetailsLocked}
                    className={FIELD_LG}
                  />
                ) : null}

                {showMaturity ? (
                  <DateInput
                    label="Maturity"
                    value={form.maturityDate}
                    onChange={(v) => handleField("maturityDate", v)}
                    disabled={positionDetailsLocked}
                    className={FIELD_SM}
                  />
                ) : null}

              </div>
            ) : null}

            <div className="flex flex-wrap gap-3 items-end">
              <DateInput
                label="Trade Date"
                value={form.tradeDate}
                onChange={(v) => handleField("tradeDate", v)}
                disabled={positionDetailsLocked}
                className={FIELD_SM}
              />

              <DateInput
                label="Settlement Date"
                value={form.settlementDate}
                onChange={(v) => handleField("settlementDate", v)}
                disabled={positionDetailsLocked}
                className={FIELD_SM}
              />

              <DestinationPositionSelector
                label="Source of Funds (optional)"
                valueLabel={selectedSourceOfFundsLabel}
                placeholder="Select source position"
                loading={isLoadingTransferDestinations}
                disabled={positionDetailsLocked}
                onBrowse={() => setIsFundingBrowserOpen(true)}
                className="min-w-72"
                controlClassName={COMPACT_INPUT}
                browseAriaLabel="Browse source of funds positions"
              />

              {form.sourceOfFundsPositionId && !positionDetailsLocked ? (
                <button
                  type="button"
                  className="btn btn-ghost !h-7 !text-xs"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      sourceOfFundsPositionId: "",
                    }))
                  }
                >
                  Clear Source
                </button>
              ) : null}

              {showClosedDate ? (
                <DateInput
                  label="Sell Date"
                  value={form.closedDate}
                  onChange={(v) => handleField("closedDate", v)}
                  disabled={positionDetailsLocked}
                  className={FIELD_SM}
                />
              ) : null}

              {isEditingInitialPosition ? (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost !h-7 !text-xs"
                    onClick={cancelInitialPositionEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary !h-7 !text-xs"
                    onClick={handleUpdateInitialPosition}
                    disabled={
                      !form.investmentType ||
                      !String(form.cusipOrId || "").trim() ||
                      hasCusipParIncreaseWarning
                    }
                  >
                    Apply to Fund
                  </button>
                </>
              ) : null}
            </div>
          </SectionCard>

          {canUsePositionWorkflows ? (
            <>
              <div>
                <TabMenu
                  tabs={POSITION_WORKFLOW_TABS}
                  activeTab={activeWorkflowTab}
                  onChange={setActiveWorkflowTab}
                  ariaLabel="Position workflow"
                />

                {activeWorkflowTab === "activity" ? (
                  <div
                    ref={activityEntryRef}
                    tabIndex={-1}
                    className="outline-none"
                  >
                    <SectionCard
                      title="Activity Entry"
                      description="Record income and position activity. Additional purchases should be added as new positions."
                    >
                      <div className="flex flex-wrap gap-3 items-end">
                        <DropdownSelect
                          label="Activity Type"
                          value={activityForm.activityType}
                          onChange={(v) => handleActivityField("activityType", v)}
                          options={activityOptions}
                          placeholder="Select..."
                          disabled={readOnly}
                          className={FIELD_MD}
                        />

                      <DateInput
                        label={isShortTermIncomeActivity ? "Period End" : "Activity Date"}
                        value={
                          isShortTermIncomeActivity
                            ? activityForm.incomePeriodEnd
                            : activityForm.activityDate
                        }
                        onChange={(v) =>
                          handleActivityField(
                            isShortTermIncomeActivity
                              ? "incomePeriodEnd"
                              : "activityDate",
                            v
                          )
                        }
                        disabled={readOnly}
                        className={FIELD_SM}
                      />

                      {isShortTermIncomeActivity ? (
                        <DateInput
                          label="Period Start"
                          value={activityForm.incomePeriodStart}
                          onChange={(v) =>
                            handleActivityField("incomePeriodStart", v)
                          }
                          disabled={readOnly}
                          className={FIELD_SM}
                        />
                      ) : null}

                      <NumberInput
                        label={
                          isTransferActivity
                            ? "Transfer Amount"
                            : isIncomeActivity
                            ? "Interest Amount"
                            : isShortTermBalanceActivity
                            ? "Amount"
                            : "Units / Amount"
                        }
                        value={activityForm.unitsAmount}
                        onChange={handleActivityUnitsAmountChange}
                        type="decimal"
                        disabled={readOnly}
                        className={FIELD_AMOUNT}
                      />

                      {isTransferActivity ? (
                        <DestinationPositionSelector
                          label={isTransferInActivity ? "Source Position" : "Destination Position"}
                          valueLabel={selectedTransferDestinationLabel}
                          placeholder={isTransferInActivity ? "Select source" : "Select destination"}
                          loading={isLoadingTransferDestinations}
                          disabled={readOnly}
                          onBrowse={openTransferBrowser}
                          className="min-w-72"
                          controlClassName={COMPACT_INPUT}
                          browseAriaLabel={isTransferInActivity ? "Browse source positions" : "Browse destination positions"}
                        />
                      ) : null}

                      {isIncomeActivity && canReinvestInterest ? (
                        <label className="h-8 flex items-center gap-2 text-sm text-app">
                          <input
                            type="checkbox"
                            checked={!!activityForm.reinvestInterest}
                            onChange={(e) =>
                              handleActivityField(
                                "reinvestInterest",
                                e.target.checked
                              )
                            }
                            disabled={readOnly}
                            className="h-4 w-4 rounded border-input"
                          />
                          <span>Reinvest Interest?</span>
                        </label>
                      ) : null}

                      {isShortTermIncomeActivity &&
                      canReinvestInterest &&
                      activityForm.reinvestInterest ? (
                        <DateInput
                          label="Posting Date"
                          value={activityForm.postingDate}
                          onChange={(v) =>
                            handleActivityField("postingDate", v)
                          }
                          disabled={readOnly}
                          className={FIELD_SM}
                        />
                      ) : null}

                      {showActivityPriceFields ? (
                        <>
                          <NumberInput
                            label="Price"
                            value={activityForm.price}
                            onChange={handleActivityPriceChange}
                            type="decimal"
                            precision={8}
                            disabled={readOnly}
                            className={FIELD_RATE}
                          />

                          <NumberInput
                            label="Cash Proceeds"
                            value={activityForm.cashProceeds}
                            onChange={handleCashProceedsChange}
                            type="currency"
                            precision={2}
                            disabled={readOnly}
                            className={FIELD_AMOUNT}
                          />
                        </>
                      ) : null}

                      {!readOnly &&
                      (isTransferActivity ? onSaveTransfer : onSaveActivity) ? (
                        <button
                          type="button"
                          className="btn btn-primary !h-7 !text-xs"
                          onClick={handleSaveActivity}
                        >
                          {workflowEditingEntry?.history_type === "activity"
                            ? isTransferActivity
                              ? "Update Transfer"
                              : "Update Activity"
                            : isTransferActivity
                              ? "Record Transfer"
                              : "Record Activity"}
                        </button>
                      ) : null}

                      {workflowEditingEntry ? (
                        <button
                          type="button"
                          className="btn btn-ghost !h-7 !text-xs"
                          onClick={clearHistoryEdit}
                        >
                          Cancel Edit
                        </button>
                      ) : null}
                    </div>

                    {activityValidationError ? (
                      <div
                        role="alert"
                        className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
                      >
                        {activityValidationError}
                      </div>
                    ) : null}

                    {isSlgsDemandDeposit ? (
                      <div className="border-t border-border pt-3">
                        <div className="text-xs text-muted mb-2">
                          <span className="font-semibold text-app">
                            Demand Deposit SLGS:
                          </span>{" "}
                          Automatically generate daily interest schedule from
                          last Interest Earned entry date.
                        </div>
                        <div className="flex flex-wrap gap-3 items-end">
                          <DateInput
                            label="Interest End Date"
                            value={slgsAccrualForm.endDate}
                            onChange={(v) =>
                              handleSlgsAccrualField("endDate", v)
                            }
                            disabled={readOnly}
                            className={FIELD_SM}
                          />

                          {!readOnly && onGenerateSlgsDemandAccruals ? (
                            <button
                              type="button"
                              className="btn btn-secondary !h-7 !text-xs"
                              onClick={handleGenerateSlgsDemandAccruals}
                            >
                              Generate Daily Interest
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    </SectionCard>
                  </div>
                ) : (
                  <SectionCard
                    title="Valuation Update"
                    description={
                      usesBalanceValuation
                        ? "Set the position's ending balance. The difference is recorded as a deposit or withdrawal."
                        : "Record an as-of price, market value, or credit rating."
                    }
                  >
                    <div className="flex flex-wrap gap-3 items-end">
                      <DateInput
                        label="Valuation Date"
                        value={valuationForm.valuationDate}
                        onChange={(v) => handleValuationField("valuationDate", v)}
                        disabled={readOnly}
                        className={FIELD_SM}
                      />

                      {!usesBalanceValuation && isCusipType ? (
                        <NumberInput
                          label="Price"
                          value={valuationForm.price}
                          onChange={handleValuationPriceChange}
                          type="decimal"
                          precision={8}
                          disabled={readOnly}
                          className={FIELD_RATE}
                        />
                      ) : null}

                      <NumberInput
                        label={usesBalanceValuation ? "Updated Balance" : "Market Value"}
                        value={valuationForm.marketValue}
                        onChange={handleValuationMarketValueChange}
                        type="currency"
                        precision={2}
                        disabled={readOnly}
                        className={FIELD_AMOUNT}
                      />

                      <TextInput
                        label="Credit Rating"
                        value={valuationForm.creditRating}
                        onChange={(v) => handleValuationField("creditRating", v)}
                        placeholder="e.g., AAA"
                        disabled={readOnly}
                        className={FIELD_XS}
                      />

                      {canOverrideEffectiveYield ? (
                        <>
                          <NumberInput
                            label="Effective Yield Override"
                            value={valuationForm.effectiveYieldOverride}
                            onChange={(v) =>
                              handleValuationField("effectiveYieldOverride", v)
                            }
                            type="percent"
                            precision={3}
                            disabled={readOnly}
                            className={FIELD_RATE}
                          />
                          <DateInput
                            label="Yield Period Start"
                            value={valuationForm.effectiveYieldStartDate}
                            onChange={(v) =>
                              handleValuationField("effectiveYieldStartDate", v)
                            }
                            disabled={readOnly}
                            className={FIELD_SM}
                          />
                          <DateInput
                            label="Yield Period End"
                            value={valuationForm.effectiveYieldEndDate}
                            onChange={(v) =>
                              handleValuationField("effectiveYieldEndDate", v)
                            }
                            disabled={readOnly}
                            className={FIELD_SM}
                          />
                        </>
                      ) : null}

                      {!readOnly &&
                      (usesBalanceValuation &&
                      workflowEditingEntry?.history_type !== "valuation"
                        ? onSaveActivity
                        : onSaveValuation) ? (
                        <button
                          type="button"
                          className="btn btn-primary !h-7 !text-xs"
                          onClick={handleSaveValuation}
                          disabled={hasInvalidEffectiveYieldOverride}
                        >
                          {workflowEditingEntry?.history_type === "valuation"
                            ? "Update Valuation"
                            : "Record Valuation"}
                        </button>
                      ) : null}

                      {workflowEditingEntry ? (
                        <button
                          type="button"
                          className="btn btn-ghost !h-7 !text-xs"
                          onClick={clearHistoryEdit}
                        >
                          Cancel Edit
                        </button>
                      ) : null}
                    </div>
                    {activityValidationError ? (
                      <div
                        role="alert"
                        className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
                      >
                        {activityValidationError}
                      </div>
                    ) : null}
                  </SectionCard>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h4 className="text-app font-semibold">Update History</h4>
                  <DateRangeSelector
                    defaultRange="30"
                    includeAll
                    onApplyFilters={handleHistoryDateRangeChange}
                    className="gap-2"
                    selectClassName="!h-7 !text-xs"
                    inputClassName="!h-7 !text-xs w-32"
                  />
                </div>
                {isLoadingActivityHistory ? (
                  <div className="text-xs text-muted">Loading history...</div>
                ) : filteredHistoryRows.length ? (
                  <ExcelGrid
                    columns={historyColumns}
                    rows={filteredHistoryRows}
                    onChange={() => {}}
                  />
                ) : (
                  <div className="text-xs text-muted">
                    {historyRows.length
                      ? "No activity or valuation updates in the selected date range."
                      : "No activity or valuation updates recorded yet."}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            {isEditMode && !readOnly && !isEditingInitialPosition ? (
              <>
                <button
                  type="button"
                  className="btn btn-ghost text-red-600 hover:text-red-700"
                  onClick={onDelete}
                >
                  Delete
                </button>
              </>
            ) : null}
            {actionNotice ? (
              <span className="text-xs font-medium text-emerald-700">
                {actionNotice}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {isEditMode &&
            isPersistedPosition &&
            !readOnly &&
            String(form.investmentType || "").trim().toLowerCase() === "lgip" &&
            smartDocAccess.enabled ? (
              <button
                type="button"
                className="btn btn-ghost !h-7 !text-xs"
                onClick={() => setIsSmartDocOpen(true)}
              >
                SmartDoc
              </button>
            ) : null}
            <button type="button" className="btn btn-ghost" onClick={handleClose}>
              {isEditMode ? "Close" : "Cancel"}
            </button>
            {!readOnly && !isEditMode ? (
              <button
                type="button"
                className="btn btn-primary !h-7 !text-xs"
                onClick={handleSave}
                disabled={
                  !form.investmentType ||
                  !String(form.cusipOrId || "").trim() ||
                  hasCusipParIncreaseWarning
                }
              >
                Add to Fund
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <DestinationPositionBrowser
        isOpen={isTransferBrowserOpen}
        targets={transferDestinations}
        selectedTargetId={isTransferInActivity ? activityForm.sourcePositionId : activityForm.destinationPositionId}
        searchText={transferSearchText}
        expandedGroupIds={expandedTransferFundIds}
        onSearchChange={setTransferSearchText}
        onToggleGroup={toggleTransferFund}
        onSelectTarget={handleSelectTransferDestination}
        onClose={closeTransferBrowser}
        title={isTransferInActivity ? "Select Source Position" : "Select Destination Position"}
        description=""
        showSearch={false}
        compact
      />
      <DestinationPositionBrowser
        isOpen={isFundingBrowserOpen}
        targets={transferDestinations}
        selectedTargetId={form.sourceOfFundsPositionId}
        searchText={fundingSearchText}
        expandedGroupIds={expandedFundingFundIds}
        onSearchChange={setFundingSearchText}
        onToggleGroup={(groupId) =>
          setExpandedFundingFundIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
          })
        }
        onSelectTarget={(position) => {
          setForm((prev) => ({
            ...prev,
            sourceOfFundsPositionId: transferDestinationId(position),
          }));
          setIsFundingBrowserOpen(false);
        }}
        onClose={() => setIsFundingBrowserOpen(false)}
        title="Select Source of Funds"
        description="Select the cash-like position funding this purchase."
        showSearch={false}
        compact
      />
      <SmartDocDialog
        isOpen={isSmartDocOpen}
        onClose={() => setIsSmartDocOpen(false)}
        workflowCode="fund-planner.lgip-monthly-statement"
        context={{
          orgId: selectedOrg?.id,
          tenantId: selectedOrg?.tenant_id || profile?.tenant_id,
          userId: profile?.id,
          fundId: initialValues?.fundId,
          fundName: initialValues?.fundName,
          positionId: initialValues?.positionId,
          positionLabel: form.cusipOrId || form.description || "LGIP position",
          investmentType: form.investmentType,
          transferPositions: transferDestinations,
        }}
        onComplete={async (result) => {
          await onSmartDocComplete?.(result);
          setIsSmartDocOpen(false);
        }}
      />
    </ModalBackdrop>
  );
}
